import { createFileRoute, Outlet } from '@tanstack/react-router';
import { BRAND_NAME, CONTENT_ONLY, brandMark } from '@/lib/ui-mode';

/**
 * Split-screen auth shell: the form owns the left column, an editorial brand
 * panel owns the right. Typography only — no stock photography.
 */
function AuthLayout() {
  // Editors get a single centred form: no product pitch, no feature list.
  if (CONTENT_ONLY) {
    return (
      <div className="cs-ground flex items-center justify-center">
        <main className="cs-card w-full max-w-md p-8 sm:p-10">
          <div className="mb-8 flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex size-11 items-center justify-center rounded-full bg-accent text-micro font-bold text-accent-ink"
            >
              {brandMark()}
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-body font-semibold tracking-tight text-ink">{BRAND_NAME}</span>
              <span className="text-micro text-ink-subtle">Website editor</span>
            </span>
          </div>
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <main className="flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-sm bg-ink text-micro font-bold text-ink-inverse">
              SF
            </span>
            <span className="text-data font-semibold tracking-tight text-ink">SiteFlow CMS</span>
          </div>
          <Outlet />
        </div>
      </main>

      <aside className="relative hidden overflow-hidden border-l border-hairline bg-sunken lg:block">
        <div aria-hidden="true" className="rule-grid absolute inset-0 opacity-60" />
        <div className="relative flex h-full flex-col justify-between px-12 py-14">
          <span className="section-label">Content operations, not another rebuild</span>

          <div className="max-w-lg">
            <p className="text-display font-semibold leading-tight tracking-tight text-ink">
              Your marketing site already exists.
              <br />
              <span className="text-ink-muted">Give the people who own the words a way in.</span>
            </p>
            <p className="mt-6 max-w-md text-body leading-relaxed text-ink-muted">
              SiteFlow layers a governed editing surface over sites you have already shipped —
              versioned content, scoped roles, and a publish trail you can audit.
            </p>
          </div>

          <dl className="grid grid-cols-3 gap-6 border-t border-hairline pt-6">
            {[
              { term: 'Roles', detail: 'Seven system roles, per-permission' },
              { term: 'Versions', detail: 'Every publish is restorable' },
              { term: 'Audit', detail: 'Who changed what, and when' },
            ].map((item) => (
              <div key={item.term}>
                <dt className="section-label">{item.term}</dt>
                <dd className="mt-1 text-data leading-snug text-ink-muted">{item.detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      </aside>
    </div>
  );
}

export const Route = createFileRoute('/(auth)')({
  component: AuthLayout,
});
